/**
 * Workspaces and invitations (Figma 09 · Workspaces and invitations 01–10).
 * Not built, no backend yet: leaving a workspace, Undo on a decline, ended
 * invitations, and brand counts / since / invited-by for other workspaces.
 */
import { useState } from "react";
import { ArrowRight, Check, Hourglass, LogOut } from "lucide-react";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useGetBrands } from "@/hooks/useGetBrands";
import {
  useAcceptInvite,
  useDeclineInvite,
  useMyInvites,
  useSwitchOrganization,
} from "@/app/routes/dashboard/settings/api/useOrganization";
import type { IncomingOrganizationInvite, UserOrganization } from "@/types/organization.type";
import { KitButton } from "../../kit/Button";
import { ErrorState } from "../../ViewStates";
import {
  DetailCard,
  DetailHead,
  Fact,
  Facts,
  GroupLabel,
  IndexCard,
  IndexRow,
  PageHead,
  QuietNote,
  ScopeTile,
  SettingsPage,
  Split,
  SplitLoading,
  StatusLabel,
  TextAction,
} from "../parts/SettingsParts";
import { useYouHead } from "../parts/scope";
import { roleForYou, roleLabel, takesSeat } from "../parts/roles";
import { formatDay, listJoin } from "../parts/settingsFormat";

type Selection = { kind: "invite"; id: string } | { kind: "org"; id: string };

function InvitationDetail({ invite }: { invite: IncomingOrganizationInvite }) {
  const accept = useAcceptInvite();
  const decline = useDeclineInvite();
  const brands = (invite.brands ?? []).map((b) => b.name);
  const inviter = invite.inviter.name || invite.inviter.email || "Someone";
  return (
    <DetailCard>
      <DetailHead
        lead={<ScopeTile name={invite.organization.name} size={56} />}
        title={invite.organization.name}
        sub={`Invitation from ${inviter}`}
        aside={<StatusLabel icon={Hourglass}>Waiting for you</StatusLabel>}
      />
      <Facts>
        <Fact label="You would be">{roleForYou(invite.role)}</Fact>
        {brands.length > 0 ? <Fact label="Brands">{listJoin(brands)}</Fact> : null}
        <Fact label="Invited by">
          {[invite.inviter.name, invite.inviter.email].filter(Boolean).join(", ") || "Unknown"}
        </Fact>
        <Fact label="Ends">{formatDay(invite.expiresAt)}</Fact>
      </Facts>
      <div className="flex items-center gap-3">
        <KitButton onClick={() => accept.mutate(invite.id)} isDisabled={accept.isPending}>
          Accept
        </KitButton>
        <KitButton variant="ghost" onClick={() => decline.mutate(invite.id)} isDisabled={decline.isPending}>
          Decline
        </KitButton>
      </div>
      <QuietNote>
        {takesSeat(invite.role)
          ? `Accepting takes a seat in ${invite.organization.name}.`
          : `Accepting opens ${invite.organization.name}. A client takes no seat.`}
      </QuietNote>
    </DetailCard>
  );
}

function WorkspaceDetail({ membership, isHere }: { membership: UserOrganization; isHere: boolean }) {
  const switchOrganization = useSwitchOrganization();
  const { data: brands } = useGetBrands({ enabled: isHere });
  const org = membership.organization;
  const isOwner = membership.role === "Owner";
  const sub = isOwner ? "You are the owner" : `You are ${membership.role === "Guest" ? "a client" : `a ${membership.role}`} here`;
  return (
    <DetailCard>
      <DetailHead
        lead={<ScopeTile name={org.name} size={56} />}
        title={org.name}
        sub={sub}
        aside={
          isHere ? (
            <StatusLabel icon={Check}>You are here</StatusLabel>
          ) : (
            <KitButton variant="secondary" onClick={() => switchOrganization(org.id)}>
              Switch to it
            </KitButton>
          )
        }
      />
      <Facts>
        <Fact label="Your role">{roleForYou(membership.role)}</Fact>
        {isHere && brands ? (
          <Fact label="Brands">
            {membership.role === "Guest" ? listJoin(brands.map((b) => b.name)) || "None yet" : String(brands.length)}
          </Fact>
        ) : null}
        {org.createdAt && isOwner ? <Fact label="Since">{formatDay(org.createdAt)}</Fact> : null}
        {!takesSeat(membership.role) ? <Fact label="Seat">Takes no seat</Fact> : null}
      </Facts>
      {isOwner ? (
        <>
          <QuietNote>An owner cannot leave. To hand the workspace over, ask Mohtawa.</QuietNote>
          {isHere ? <TextAction icon={ArrowRight} to=".?page=general">
              Open General
            </TextAction> : null}
        </>
      ) : null}
    </DetailCard>
  );
}

export default function WorkspacesPage() {
  const head = useYouHead();
  const { user, selectedOrganization, logout } = useCurrentUser();
  const { data: invites, isLoading, isError, refetch } = useMyInvites();
  const memberships = user?.userOrganizations ?? [];
  const pending = invites ?? [];
  const [picked, setPicked] = useState<Selection | null>(null);

  const currentId = selectedOrganization?.organization.id ?? null;
  const fallback: Selection | null = pending[0]
    ? { kind: "invite", id: pending[0].id }
    : currentId
      ? { kind: "org", id: currentId }
      : memberships[0]
        ? { kind: "org", id: memberships[0].organization.id }
        : null;
  const valid =
    picked &&
    (picked.kind === "invite"
      ? pending.some((i) => i.id === picked.id)
      : memberships.some((m) => m.organization.id === picked.id));
  const selection = valid ? picked : fallback;

  const status =
    pending.length === 1
      ? "One invitation is waiting for you."
      : pending.length > 1
        ? `${pending.length} invitations are waiting for you.`
        : "The workspaces you belong to, and invitations waiting for you.";

  const selectedInvite = selection?.kind === "invite" ? pending.find((i) => i.id === selection.id) : undefined;
  const selectedMembership =
    selection?.kind === "org" ? memberships.find((m) => m.organization.id === selection.id) : undefined;

  return (
    <SettingsPage>
      <PageHead {...head} title="Workspaces and invitations" status={status} />
      {isLoading ? (
        <SplitLoading caption="Loading your workspaces" />
      ) : isError ? (
        <ErrorState
          title="Your workspaces did not load"
          body="Creo could not read them. Nothing has changed."
          onRetry={() => void refetch()}
        />
      ) : (
        <Split
          backLabel="Workspaces"
          index={
            <IndexCard>
              {pending.length > 0 ? (
                <>
                  <GroupLabel>Invitations for you</GroupLabel>
                  {pending.map((invite) => (
                    <IndexRow
                      key={invite.id}
                      lead={<ScopeTile name={invite.organization.name} />}
                      title={invite.organization.name}
                      sub={`Invitation · expires ${formatDay(invite.expiresAt)}`}
                      active={selection?.kind === "invite" && selection.id === invite.id}
                      onPress={() => setPicked({ kind: "invite", id: invite.id })}
                    />
                  ))}
                </>
              ) : null}
              <GroupLabel>Your workspaces</GroupLabel>
              {memberships.map((m) => (
                <IndexRow
                  key={m.organization.id}
                  lead={<ScopeTile name={m.organization.name} />}
                  title={m.organization.name}
                  sub={[roleLabel(m.role), m.organization.id === currentId ? "you are here" : null]
                    .filter(Boolean)
                    .join(" · ")}
                  active={selection?.kind === "org" && selection.id === m.organization.id}
                  onPress={() => setPicked({ kind: "org", id: m.organization.id })}
                />
              ))}
            </IndexCard>
          }
          detail={
            selectedInvite ? (
              <InvitationDetail invite={selectedInvite} />
            ) : selectedMembership ? (
              <WorkspaceDetail membership={selectedMembership} isHere={selectedMembership.organization.id === currentId} />
            ) : (
              <DetailCard>
                <p className="text-[14px] text-co-text2">No invitations for this address.</p>
                <QuietNote>
                  You are signed in as {user?.email}. An invitation goes to one address: if yours went to another,
                  sign in with that one, or ask the sender to invite this address.
                </QuietNote>
                <TextAction icon={LogOut} onPress={logout}>
                  Sign in with another address
                </TextAction>
              </DetailCard>
            )
          }
        />
      )}
    </SettingsPage>
  );
}
